import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import valid from 'card-validator';
import { useTranslation } from 'react-i18next';
import parseMax from 'libphonenumber-js/max'

import {
  registerCustomCards,
} from '../utils/registerCustomCards';
import { useWidth } from './useWidth';
import { COUNTRIES } from '../constants/countries';
import { useSafeSelector } from './useSafeSelector';
import { AMEX_CARD_LENGTH, AMEX_CVV_LENGTH, CARD_LENGTH, CARD_NUMBER_SPACES, CVV_LENGTH, EXPIRY_DATE_LENGTH } from '../constants';
import { CARD_TYPES } from '../constants/card';

export interface useHookFormProps {
  isCard?: boolean;
  isExpiry?: boolean;
  isCvv?: boolean;
  isPremium?: boolean;
  isName?: boolean;
  isFullName?: boolean;
  isEmail?: boolean;
  isPhone?: { name: string; code: string };
  isNationalId?: boolean;
  isSaveCard?: boolean;
  isCNIC?: boolean;
  isHalan?: boolean;
  isJazzCashWalletNumber?: boolean;
  isInstapayId?: boolean;
  isIframe?: boolean;
  isStandardCard?: boolean;
  isSaveFastCheckout?: boolean;
  hasOmanNetIntegration?: boolean;
  countryName?: string;
  defaultValues?: any;
  noCardLogo?: boolean;
  isAmount?: boolean;
  maxAmount?: number;
}

export function useHookForm(props: useHookFormProps) {
  const {
    isCard,
    isPremium,
    isExpiry,
    isCvv,
    isName,
    isEmail,
    isIframe,
    isFullName,
    isSaveCard,
    isPhone,
    isNationalId,
    isHalan,
    isInstapayId,
    isStandardCard,
    isSaveFastCheckout,
    hasOmanNetIntegration,
    countryName,
    defaultValues,
    noCardLogo,
    isAmount,
    maxAmount
  } = props || {};
  const [cardType, setCardType] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [country, setCountry] = useState(isPhone);
  const intentionData = useSafeSelector((state: any) => state.intention);
  const isMobile = useWidth();
  const { t } = useTranslation('card');
  const {
    register,
    handleSubmit,
    resetField,
    watch,
    formState: { errors, touchedFields, dirtyFields, isDirty, isValid },
    getFieldState,
    setValue,
    getValues,
    setFocus,
    trigger,
    clearErrors,
    reset,
    unregister,
  } = useForm({ mode: 'onBlur', defaultValues });

  useEffect(() => {
    if (isCard) setFocus('number');
    else if (isExpiry) setFocus('expiry');
    else if (isCvv) setFocus('cvc');
    else if (isName) setFocus('name');
  }, []);

  useEffect(() => {
    if (isPhone) {
      setCountry(isPhone);
      resetField('phone');
    } else {
      setCountry(undefined);
    }
  }, [isPhone]);

  if (isStandardCard) {
    const cardNumberRegister = register('number', {
      onChange: (e) => {
        if (!isMobile) {
          clearErrors('number');
        }
        const cardNumber = e.target.value;
        const trimmedNumber = cardNumber.replace(/\D/g, '');
        const displayValue = trimmedNumber
          .replace(/(.{4})/g, '$1 ') // Add white space after each 4 digits
          .replace(/\s?$/, ''); // Remove extra white spaces
        setValue('number', displayValue);
        registerCustomCards?.({ card: trimmedNumber, noCardLogo });
        const numberValidation = valid.number(trimmedNumber);
        if (numberValidation.card) {
          if (
            numberValidation.card.type === 'omannet' &&
            trimmedNumber.length < 6
          ) {
            setCardType('');
          } else {
            setCardType(numberValidation.card.type);
          }
        } else {
          setCardType('');
        }
        if (touchedFields['cvc'] && dirtyFields['cvc']) {
          trigger('cvc');
        }
        if (touchedFields['name'] && dirtyFields['name']) {
          trigger('name');
        }
        if (
          cardType === CARD_TYPES.AMEX
            ? trimmedNumber.length === AMEX_CARD_LENGTH
            : trimmedNumber.length === CARD_LENGTH
        ) {
          setFocus('expiry', { shouldSelect: getValues('expiry') });
        }
        if (!cardNumber) {
          resetField('number');
        }
      },
      required: {
        value: true,
        message: t('CARD_NUMBER_IS_REQUIRED'),
      },
      minLength: {
        value:
          cardType === CARD_TYPES.AMEX
            ? AMEX_CARD_LENGTH + CARD_NUMBER_SPACES
            : CARD_LENGTH + CARD_NUMBER_SPACES,
        message: t('CARD_NUMBER_NOT_VALID'),
      },
      validate: (value) => {
        const trimmedNumber = value.replace(/ /g, '');
        const numberValidation = valid.number(trimmedNumber);
        if (
          numberValidation.card?.type === CARD_TYPES.OMAN &&
          !hasOmanNetIntegration &&
          (intentionData.country === COUNTRIES.OMAN ||
            countryName === COUNTRIES.OMAN)
        ) {
          return t('ERR_OMAN_NET_INVALID');
        }
        return numberValidation.isValid || t('CARD_NUMBER_NOT_VALID');
      },
    });
    const expiryRegister = register('expiry', {
      onChange: (e) => {
        clearErrors('expiry');
        const isRemoving =
          expiryDate.length === 3 && e.target.value.length === 2;
        const expDate = e.target.value
          .replace(
            /^([1-9]\/|[2-9])$/g,
            isRemoving ? '0$1' : '0$1/' // 3 > 03/
          )
          .replace(
            /^(0[1-9]|1[0-2])$/g,
            isRemoving ? '$1' : '$1/' // 11 > 11/
          )
          .replace(
            /^([0-1])([3-9])$/g,
            '0$1/$2' // 13 > 01/3
          )
          .replace(
            /^(0?[1-9]|1[0-2])([0-9]{2})$/g,
            '$1/$2' // 141 > 01/41
          )
          .replace(
            /^([0]+)\/|[0]+$/g,
            '0' // 0/ > 0 and 00 > 0
          )
          .replace(
            /[^\d\\/]|^[\\/]*$/g,
            '' // To allow only digits and `/`
          )
          .replace(
            /\/\//g,
            '/' // Prevent entering more than 1 `/`
          );
        const expDateFormatter =
          expDate.replace(/\//g, '').substring(0, 2) +
          (expDate.length > 2 ? '/' : '') +
          expDate.replace(/\//g, '').substring(2, 4);
        setExpiryDate(expDateFormatter);
        setValue('expiry', expDateFormatter);
        if (expDateFormatter.length === EXPIRY_DATE_LENGTH) {
          setFocus('cvc');
        }
        if (!e.target.value) {
          resetField('expiry');
        }
      },
      required: {
        value: true,
        message: t('CARD_EXPIRY_IS_REQUIRED'),
      },
      minLength: {
        value: EXPIRY_DATE_LENGTH,
        message: t('CARD_EXPIRY_NOT_VALID'),
      },
      pattern: {
        value: /^(0[1-9]|1[0-2])\/?([0-9]{2})$/gm,
        message: t('CARD_EXPIRY_NOT_VALID'),
      },
      validate: (value: any) => {
        const expiryDateValidation = valid.expirationDate(value);
        return expiryDateValidation.isValid || t('CARD_EXPIRY_NOT_VALID');
      },
    });
    const cvcRegister = register('cvc', {
      onChange: (e) => {
        clearErrors('cvc');
        const cvv = e.target.value;
        const trimmedCvv = cvv.replace(/\D/g, '');
        setValue('cvc', trimmedCvv);
        if (
          cardType === CARD_TYPES.AMEX
            ? trimmedCvv.length === AMEX_CVV_LENGTH
            : trimmedCvv.length === CVV_LENGTH
        ) {
          setFocus('name');
        }
        if (!e.target.value) {
          resetField('cvc');
          setFocus('expiry');
        }
      },
      required: {
        value: true,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
      minLength: {
        value: cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
      maxLength: {
        value: cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
    });
    const nameRegister = register('name', {
      onChange: (e) => {
        clearErrors('name');
        const name = e.target.value;
        const nameRegex = /^((?:[A-Za-z]+ ?){1,3})$/;
        const displayValue = name
          .replace(/[^a-zA-Z ]/g, '')
          .replace(/\s+/g, ' ');
        if (nameRegex.test(displayValue)) {
          setValue('name', displayValue.toUpperCase());
          trigger('name');
        } else {
          setValue('name', displayValue.trim().toUpperCase());
          trigger('name');
        }
        if (!e.target.value) {
          resetField('name');
          setFocus('cvc');
        }
      },
      required: {
        value: true,
        message: t('CARD_NAME_IS_REQUIRED'),
      },
      validate: (value) => {
        const nameValidation = valid.cardholderName(value.toLowerCase());
        return nameValidation.isValid || t('CARD_NAME_NOT_VALID');
      },
    });
    const saveCardRegister = register('save_card');
    const amountRegister = isAmount && register('amount', {
      onChange: (e) => {
        clearErrors('amount');
        setValue('amount', e.target.value);
        if (!e.target.value) {
          resetField('amount');
        }
      },
      required: {
        value: true,
        message: t('AMOUNT_IS_REQUIRED'),
      },
      validate: (value) => {
        const regex = /^[0-9]*\.?[0-9]+$/;
        if (+value === 0) {
          return t('AMOUNT_ZERO_VALIDATION');
        }
        if (!regex.test(value)) {
          return t('AMOUNT_NOT_VALID');
        }
        if (maxAmount && +value > maxAmount) {
          return t('AMOUNT_EXCEEDS_MAXIMUM_VALIDATION');
        }
        return true;
      }
    })
    
    return {
      handleSubmit,
      errors,
      isDirty,
      isValid,
      trigger,
      cardNumberRegister,
      expiryRegister,
      cvcRegister,
      nameRegister,
      saveCardRegister,
      getFieldState,
      setFocus,
      resetField,
      watch,
      getValues,
      clearErrors,
      cardType,
      amountRegister,
      setValue,
      reset,
      unregister
    };
  }
  
  if (isIframe) {
    const cardNumberRegister = register('number', {
      onChange: (e) => {
        if (!isMobile) {
          clearErrors('number');
        }
        const cardNumber = e.target.value;
        const trimmedNumber = cardNumber.replace(/\D/g, '');
        const displayValue = trimmedNumber
          .replace(/(.{4})/g, '$1 ') // Add white space after each 4 digits
          .replace(/\s?$/, ''); // Remove extra white spaces
        setValue('number', displayValue);
        registerCustomCards?.({ card: trimmedNumber, noCardLogo });
        const numberValidation = valid.number(trimmedNumber);
        if (numberValidation.card) {
          if (
            numberValidation.card.type === 'omannet' &&
            trimmedNumber.length < 6
          ) {
            setCardType('');
          } else {
            setCardType(numberValidation.card.type);
          }
        } else {
          setCardType('');
        }
        if (touchedFields['cvc'] && dirtyFields['cvc']) {
          trigger('cvc');
        }
        if (touchedFields['name'] && dirtyFields['name']) {
          trigger('name');
        }
        if (
          cardType === CARD_TYPES.AMEX
            ? trimmedNumber.length === AMEX_CARD_LENGTH
            : trimmedNumber.length === CARD_LENGTH
        ) {
          setFocus('expiry', { shouldSelect: getValues('expiry') });
        }
        if (!cardNumber) {
          resetField('number');
        }
      },
      required: {
        value: true,
        message: t('CARD_NUMBER_IS_REQUIRED'),
      },
      minLength: {
        value:
          cardType === CARD_TYPES.AMEX
            ? AMEX_CARD_LENGTH + CARD_NUMBER_SPACES
            : CARD_LENGTH + CARD_NUMBER_SPACES,
        message: t('CARD_NUMBER_NOT_VALID'),
      },
      validate: (value) => {
        const trimmedNumber = value.replace(/ /g, '');
        const numberValidation = valid.number(trimmedNumber);
        if (
          numberValidation.card?.type === CARD_TYPES.OMAN &&
          !hasOmanNetIntegration &&
          (intentionData.country === COUNTRIES.OMAN ||
            countryName === COUNTRIES.OMAN)
        ) {
          return t('ERR_OMAN_NET_INVALID');
        }
        return numberValidation.isValid || t('CARD_NUMBER_NOT_VALID');
      },
    });
    const expiryRegister = register('expiry', {
      onChange: (e) => {
        clearErrors('expiry');
        const isRemoving =
          expiryDate.length === 3 && e.target.value.length === 2;
        const expDate = e.target.value
          .replace(
            /^([1-9]\/|[2-9])$/g,
            isRemoving ? '0$1' : '0$1/' // 3 > 03/
          )
          .replace(
            /^(0[1-9]|1[0-2])$/g,
            isRemoving ? '$1' : '$1/' // 11 > 11/
          )
          .replace(
            /^([0-1])([3-9])$/g,
            '0$1/$2' // 13 > 01/3
          )
          .replace(
            /^(0?[1-9]|1[0-2])([0-9]{2})$/g,
            '$1/$2' // 141 > 01/41
          )
          .replace(
            /^([0]+)\/|[0]+$/g,
            '0' // 0/ > 0 and 00 > 0
          )
          .replace(
            /[^\d\\/]|^[\\/]*$/g,
            '' // To allow only digits and `/`
          )
          .replace(
            /\/\//g,
            '/' // Prevent entering more than 1 `/`
          );
        const expDateFormatter =
          expDate.replace(/\//g, '').substring(0, 2) +
          (expDate.length > 2 ? '/' : '') +
          expDate.replace(/\//g, '').substring(2, 4);
        setExpiryDate(expDateFormatter);
        setValue('expiry', expDateFormatter);
        if (expDateFormatter.length === EXPIRY_DATE_LENGTH) {
          setFocus('cvc');
        }
        if (!e.target.value) {
          resetField('expiry');
        }
      },
      required: {
        value: true,
        message: t('CARD_EXPIRY_IS_REQUIRED'),
      },
      minLength: {
        value: EXPIRY_DATE_LENGTH,
        message: t('CARD_EXPIRY_NOT_VALID'),
      },
      pattern: {
        value: /^(0[1-9]|1[0-2])\/?([0-9]{2})$/gm,
        message: t('CARD_EXPIRY_NOT_VALID'),
      },
      validate: (value: any) => {
        const expiryDateValidation = valid.expirationDate(value);
        return expiryDateValidation.isValid || t('CARD_EXPIRY_NOT_VALID');
      },
    });
    const cvcRegister = register('cvc', {
      onChange: (e) => {
        clearErrors('cvc');
        const cvv = e.target.value;
        const trimmedCvv = cvv.replace(/\D/g, '');
        setValue('cvc', trimmedCvv);
        if (
          cardType === CARD_TYPES.AMEX
            ? trimmedCvv.length === AMEX_CVV_LENGTH
            : trimmedCvv.length === CVV_LENGTH
        ) {
          setFocus('name');
        }
        if (!e.target.value) {
          resetField('cvc');
          setFocus('expiry');
        }
      },
      required: {
        value: true,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
      minLength: {
        value: cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
      maxLength: {
        value: cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
    });

    return {
      handleSubmit,
      errors,
      isDirty,
      isValid,
      trigger,
      cardNumberRegister,
      expiryRegister,
      cvcRegister,
      getFieldState,
      setFocus,
      resetField,
      watch,
      getValues,
      clearErrors,
      cardType,
    };
  }

  const cardNumberRegister =
    isCard &&
    register('number', {
      onChange: (e) => {
        clearErrors('number');
        const cardNumber = e.target.value;
        const trimmedNumber = cardNumber.replace(/\D/g, '');
        const displayValue = trimmedNumber
          .replace(/(.{4})/g, '$1 ') // Add white space after each 4 digits
          .replace(/\s?$/, ''); // Remove extra white spaces
        setValue('number', displayValue);
        registerCustomCards?.({ card: trimmedNumber, noCardLogo });
        const numberValidation = valid.number(trimmedNumber);
        if (numberValidation.card) {
          setCardType(numberValidation.card.type);
        } else {
          setCardType('');
        }
        if (touchedFields['cvc'] && dirtyFields['cvc']) {
          trigger('cvc');
        }
        if (touchedFields['name'] && dirtyFields['name']) {
          trigger('name');
        }
        if (
          cardType === CARD_TYPES.AMEX
            ? trimmedNumber.length === AMEX_CARD_LENGTH
            : trimmedNumber.length === CARD_LENGTH
        ) {
          setFocus('expiry', { shouldSelect: getValues('expiry') });
        }
        if (!cardNumber) {
          resetField('number');
        }
      },
      required: {
        value: true,
        message: t('CARD_NUMBER_IS_REQUIRED'),
      },
      minLength: {
        value:
          cardType === CARD_TYPES.AMEX
            ? AMEX_CARD_LENGTH + CARD_NUMBER_SPACES
            : CARD_LENGTH + CARD_NUMBER_SPACES,
        message: t('CARD_NUMBER_NOT_VALID'),
      },
      validate: (value) => {
        const trimmedNumber = value.replace(/ /g, '').trim();
        const numberValidation = valid.number(trimmedNumber);
        return numberValidation.isValid || t('CARD_NUMBER_NOT_VALID');
      },
    });

  const premiumCardNumberRegister =
    isPremium &&
    register('number', {
      onChange: (e) => {
        clearErrors('number');
        const cardNumber = e.target.value;
        const trimmedNumber = cardNumber.replace(/\D/g, '');
        const displayValue = trimmedNumber
          .replace(/(.{4})/g, '$1 ') // Add white space after each 4 digits
          .replace(/\s?$/, ''); // Remove extra white spaces
        setValue('number', displayValue);
        registerCustomCards?.({ card: trimmedNumber, noCardLogo });
        // const numberValidation = valid.number(trimmedNumber);
        // if (numberValidation.card) {
        //   setCardType(numberValidation.card.type);
        // } else {
        //   setCardType('');
        // }
        if (touchedFields['cvc'] && dirtyFields['cvc']) {
          trigger('cvc');
        }
        if (touchedFields['name'] && dirtyFields['name']) {
          trigger('name');
        }
        if (trimmedNumber.length === CARD_LENGTH) {
          setFocus('expiry', { shouldSelect: getValues('expiry') });
        }
        if (!cardNumber) {
          resetField('number');
        }
      },
      required: {
        value: true,
        message: t('CARD_NUMBER_IS_REQUIRED'),
      },
      minLength: {
        value: CARD_LENGTH + CARD_NUMBER_SPACES,
        message: t('CARD_NUMBER_NOT_VALID'),
      },
      validate: (value) => {
        const regex = /^[0-9]{16}$/;
        const trimmedNumber = value.replace(/ /g, '').trim();
        return regex.test(trimmedNumber) || t('CARD_NUMBER_NOT_VALID');
      },
    });
  
  const expiryRegister =
    isExpiry &&
    register('expiry', {
      onChange: (e) => {
        clearErrors('expiry');
        const isRemoving =
          expiryDate.length === 3 && e.target.value.length === 2;
        const expDate = e.target.value
          .replace(
            /^([1-9]\/|[2-9])$/g,
            isRemoving ? '0$1' : '0$1/' // 3 > 03/
          )
          .replace(
            /^(0[1-9]|1[0-2])$/g,
            isRemoving ? '$1' : '$1/' // 11 > 11/
          )
          .replace(
            /^([0-1])([3-9])$/g,
            '0$1/$2' // 13 > 01/3
          )
          .replace(
            /^(0?[1-9]|1[0-2])([0-9]{2})$/g,
            '$1/$2' // 141 > 01/41
          )
          .replace(
            /^([0]+)\/|[0]+$/g,
            '0' // 0/ > 0 and 00 > 0
          )
          .replace(
            /[^\d\\/]|^[\\/]*$/g,
            '' // To allow only digits and `/`
          )
          .replace(
            /\/\//g,
            '/' // Prevent entering more than 1 `/`
          );
        const expDateFormatter =
          expDate.replace(/\//g, '').substring(0, 2) +
          (expDate.length > 2 ? '/' : '') +
          expDate.replace(/\//g, '').substring(2, 4);
        setExpiryDate(expDateFormatter);
        setValue('expiry', expDateFormatter);
        if (expDateFormatter.length === EXPIRY_DATE_LENGTH) {
          setFocus('cvc');
        }
        if (!e.target.value) {
          resetField('expiry');
        }
      },
      required: {
        value: true,
        message: t('CARD_EXPIRY_IS_REQUIRED'),
      },
      minLength: {
        value: EXPIRY_DATE_LENGTH,
        message: t('CARD_EXPIRY_NOT_VALID'),
      },
      pattern: {
        value: /^(0[1-9]|1[0-2])\/?([0-9]{2})$/gm,
        message: t('CARD_EXPIRY_NOT_VALID'),
      },
      validate: (value: any) => {
        const expiryDateValidation = valid.expirationDate(value);
        return expiryDateValidation.isValid || t('CARD_EXPIRY_NOT_VALID');
      },
    });
  const cvcRegister =
    isCvv &&
    register('cvc', {
      onChange: (e) => {
        clearErrors('cvc');
        const cvv = e.target.value;
        const trimmedCvv = cvv.replace(/\D/g, '');
        setValue('cvc', trimmedCvv);
        if (
          cardType === CARD_TYPES.AMEX
            ? trimmedCvv.length === AMEX_CVV_LENGTH
            : trimmedCvv.length === CVV_LENGTH
        ) {
          setFocus('name');
        }
        if (!e.target.value) {
          resetField('cvc');
        }
      },
      required: {
        value: true,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
      minLength: {
        value: cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
      maxLength: {
        value: cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH,
        message:
          cardType === CARD_TYPES.AMEX
            ? t('AMEX_CVV_IS_REQUIRED')
            : t('CVV_IS_REQUIRED'),
      },
    });
  const nameRegister =
    isName &&
    register('name', {
      onChange: (e) => {
        clearErrors('name');
        const name = e.target.value;
        const nameRegex = /^((?:[A-Za-z]+ ?){1,3})$/;
        const displayValue = name
          .replace(/[^a-zA-Z ]/g, '')
          .replace(/\s+/g, ' ');
        if (nameRegex.test(displayValue)) {
          setValue('name', displayValue.toUpperCase());
        } else {
          setValue('name', displayValue.trim().toUpperCase());
        }
        if (!e.target.value) {
          resetField('name');
        }
      },
      required: {
        value: true,
        message: t('CARD_NAME_IS_REQUIRED'),
      },
      validate: (value) => {
        const nameValidation = valid.cardholderName(value.toLowerCase());
        return nameValidation.isValid || t('CARD_NAME_NOT_VALID');
      },
    });
    const fullnameRegister =
    isFullName &&
    register('fullname', {
      onChange: (e) => {
        clearErrors('name');
        const name = e.target.value;
        const nameRegex = /^((?:[A-Za-z]+ ?){1,3})$/;
        const displayValue = name
          .replace(/[^a-zA-Z ]/g, '')
          .replace(/\s+/g, ' ');
        if (nameRegex.test(displayValue)) {
          setValue('name', displayValue.toUpperCase());
        } else {
          setValue('name', displayValue.trim().toUpperCase());
        }
        if (!e.target.value) {
          resetField('name');
        }
      },
      required: {
        value: true,
        message: t('NAME_IS_REQUIRED'),
      },
      validate: (value) => {
        const nameValidation = valid.cardholderName(value.toLowerCase());
        return nameValidation.isValid || t('CARD_NAME_NOT_VALID');
      },
    });

  const saveCardRegister = isSaveCard && register('save_card');
  const emailRegister = isEmail && register('email', {
    onChange: (e) => {
      clearErrors('email');
      setValue('email', e.target.value);
      clearErrors('email');
      if (!e.target.value) {
        resetField('email');
      }
    },
    required: {
      value: true,
      message: t('EMAIL_IS_REQUIRED'),
    },
    validate: (value) => {
      const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
      return regex.test(value) || t('EMAIL_NOT_VALID');
    }
  });
  const phoneRegister = country && register('phone', {
    onChange: (e) => {
      clearErrors('phone');
      const phoneNumber = e.target.value;
      const trimmedNumber = phoneNumber.replace(/\D/g, '');
      setValue('phone', trimmedNumber);
    },
    required: {
      value: true,
      message: t('PHONE_IS_REQUIRED'),
    },
    validate: (value) => {
      return parseMax(country.code + value)?.isValid() || t('PHONE_NOT_VALID');
    },
  });
  const nationalIdRegister = isNationalId && register('nationalId', {
    onChange: (e) => {
      clearErrors('nationalId');
      setValue('nationalId', e.target.value.replace(/\D/,''));
      clearErrors('nationalId');
      if (!e.target.value) {
        resetField('nationalId');
      }
    },
    required: {
      value: true,
      message: 'National ID is required'
    },
    validate: (value) => {
      const regex = /^\d{14}$/;
      return regex.test(value) || 'Please enter 14-digit National ID';
    }
  });
  const instapayIdRegister = isInstapayId && register('instapayId', {
    onChange: (e) => {
      clearErrors('instapayId');
      setValue('instapayId', e.target.value);
      if (!e.target.value) {
        resetField('instapayId');
      }
    },
    required: {
      value: true,
      message: 'This field is required'
    },
    validate: (value) => {
      const regex = /^[a-zA-Z0-9]+@instapay$/;
      return regex.test(value) || 'Instapay ID is not valid';
    }
  });
  const saveFastCheckoutRegister = isSaveFastCheckout && register('save_fastCheckout');

  return {
    handleSubmit,
    errors,
    isDirty,
    isValid,
    cardNumberRegister,
    expiryRegister,
    trigger,
    cvcRegister,
    nameRegister,
    setFocus,
    fullnameRegister,
    premiumCardNumberRegister,
    saveCardRegister,
    emailRegister,
    phoneRegister,
    nationalIdRegister,
    register,
    getValues,
    cardType,
    getFieldState,
    resetField,
    clearErrors,
    setValue,
    watch,
    instapayIdRegister,
    reset,
    unregister,
    saveFastCheckoutRegister
  };
}
